// The simulated draft room (DRAFT_ROOM.md, REALTIME.md): the pure engine
// from @fsp/draft hosted by the sim. The room opens 30 minutes before the
// scheduled time, starts on time whether or not the commissioner shows up,
// fills open seats with AI managers, and moves with the sim clock. When it
// completes, the league becomes an in-season league with real rosters.

import {
  formatInZone,
  positionRanks,
  positionTiers,
  type DraftRecapView,
  type DraftView,
} from '@fsp/domain';
import { BANDS, bandOf, castMember, say } from '@fsp/bots';
import {
  AI_PRESETS,
  draftRecap,
  advance,
  aiForSeat,
  apply,
  createDraft,
  currentOverall,
  currentTeam,
  draftStyleOf,
  isTaken,
  needs,
  styleRecap,
  styleWords,
  teamForPick,
  type AiManager,
  type DraftClub,
  type DraftContext,
  type DraftEvent,
  type DraftFacts,
  type DraftIntent,
  wantedTaken,
} from '@fsp/draft';
import { football, nflTeams } from '@fsp/sports-football';
import { botLineFace, chatView, readableColor, systemMessage } from './chat';
import { buildSchedule, fillLineups, type SimState, type SimTeam } from './league';
import { hashSeed, rng, talkDraws } from './rng';
import { leagueRef, playerSummary, projection, teamRef } from './views';
import { startWaiversAfterDraft } from './waivers';

const ROOM_OPENS_BEFORE_MS = 30 * 60 * 1000;

function iso(ms: number): string {
  return new Date(ms).toISOString();
}

export function draftRoomOpen(state: SimState, now: number): boolean {
  return (
    state.status === 'setup' &&
    state.draftAt !== null &&
    now >= state.draftAt - ROOM_OPENS_BEFORE_MS
  );
}

/** What the engine needs from this league: rankings by projection, positions, needs, caps. */
/** The value-over-replacement function behind the rankings, kept per state for the recap. */
const valueCache = new WeakMap<SimState, (id: string) => number>();

/** Average draft position for a player: its rank, nudged by a stable draw, as the room shows it. */
export function adpFor(rankIndex: number, playerId: string): number {
  return (
    Math.round((rankIndex + 1) * (0.9 + rng(hashSeed(playerId, 'adp')).next() * 0.25) * 10) / 10
  );
}

export function draftContext(state: SimState): DraftContext {
  const positionOf = (id: string) => state.players.get(id)?.positions[0] ?? '';
  const slotDefs = new Map(football.slots.map((s) => [s.id, s]));
  const starterNeeds: Record<string, number> = {};
  for (const { slot, count } of state.settings.roster.slots) {
    const def = slotDefs.get(slot);
    if (!def || def.kind !== 'starter' || def.eligible.length !== 1) continue;
    const pos = def.eligible[0]!;
    starterNeeds[pos] = (starterNeeds[pos] ?? 0) + count;
  }
  // Rankings by value over replacement: a player's points above the last
  // starter-quality player at his position across the league, so a 20-point
  // running back outranks a 24-point quarterback when every team starts one
  // quarterback and two backs. Ties fall back to raw points.
  const teamCount = state.teams.length;
  const byPos = new Map<string, number[]>();
  for (const p of state.players.values()) {
    const list = byPos.get(p.positions[0]) ?? [];
    list.push(p.ppg);
    byPos.set(p.positions[0], list);
  }
  const replacement = new Map<string, number>();
  for (const [pos, list] of byPos) {
    list.sort((a, b) => b - a);
    const flexShare = pos === 'RB' || pos === 'WR' || pos === 'TE' ? Math.ceil(teamCount / 2) : 0;
    const idx = Math.min(list.length - 1, (starterNeeds[pos] ?? 0) * teamCount + flexShare);
    replacement.set(pos, list[idx] ?? 0);
  }
  const value = (id: string) => {
    const p = state.players.get(id)!;
    return p.ppg - (replacement.get(p.positions[0]) ?? 0);
  };
  valueCache.set(state, value);
  const rankings = [...state.players.values()]
    .map((p) => p.id)
    .sort((a, b) => value(b) - value(a) || state.players.get(b)!.ppg - state.players.get(a)!.ppg);
  // Caps: the league's own if set, otherwise starters plus a sensible bench share.
  const maxPerPosition: Record<string, number> = { ...state.settings.roster.maxPerPosition };
  for (const pos of football.positions) {
    if (maxPerPosition[pos.id] !== undefined) continue;
    const starters = starterNeeds[pos.id] ?? 0;
    maxPerPosition[pos.id] =
      pos.group === 'offense' ? starters + (starters >= 2 ? 3 : 1) : Math.max(1, starters);
  }
  // Late positions the league starts, not every non-offense position the
  // sport has: a practice league starts no defensive players (Q11's review).
  const latePositions = football.positions
    .filter((p) => p.group !== 'offense' && (starterNeeds[p.id] ?? 0) > 0)
    .map((p) => p.id);
  // WHAT THE BOTS' STYLES READ (AI_MANAGERS.md §4.7): the practice world's
  // players have a club and the injury report, and nothing more, so a taste
  // for rookies or veterans has nobody to fall for here.
  const facts: Record<string, DraftFacts> = {};
  for (const p of state.players.values()) {
    const severity = football.injuryStatuses.find((s) => s.id === p.injury?.status)?.severity;
    facts[p.id] = {
      club: p.proTeam,
      bye: simBye(state, p.proTeam),
      rookie: false,
      years: null,
      hurt: !p.injury || severity === 'none' ? 0 : severity === 'warn' || !severity ? 1 : 2,
      games: null,
      first: p.name.split(' ')[0] ?? null,
    };
  }
  return {
    rankings,
    positionOf,
    starterNeeds,
    maxPerPosition,
    latePositions,
    random: (key) => rng(hashSeed(state.seed, key)).next(),
    // The practice room is watched by its one person, so their own clock
    // running out picks for that turn and never leaves them on auto.
    isPresent: (teamId) => state.teams.some((t) => t.id === teamId && t.isMine),
    sport: football.id,
    styleSeed: state.leagueId,
    facts,
    clubs: CLUBS,
    values: Object.fromEntries(rankings.map((id) => [id, value(id)])),
  };
}

/** The clubs as a bot's style names them. */
const CLUBS: Record<string, DraftClub> = Object.fromEntries(
  football.proTeams.map((t) => [t.id, { name: t.shortName, division: t.division ?? null }]),
);

/** The positions the practice league's roster starts. */
function startedBy(state: SimState): Set<string> {
  const out = new Set<string>();
  for (const { slot, count } of state.settings.roster.slots) {
    const def = football.slots.find((s) => s.id === slot);
    if (count > 0 && def?.kind === 'starter') for (const p of def.eligible) out.add(p);
  }
  return out;
}

/** A bot's style in a few phrases, for its seat, as the real room says it. */
function styleOfSeat(state: SimState, ai: AiManager, draftId: string): string[] {
  const ctx = {
    sport: football.id,
    styleSeed: state.leagueId,
    clubs: CLUBS,
    latePositions: football.positions
      .filter((p) => p.group !== 'offense' && startedBy(state).has(p.id))
      .map((p) => p.id),
  };
  const style = draftStyleOf(ai, ctx, draftId);
  return style === null ? [] : styleWords(style, ctx, false);
}

function seatOrder(state: SimState): string[] {
  const r = rng(hashSeed(state.seed, 'draft-order'));
  return r.shuffle(state.teams.map((t) => t.id));
}

/** Open the room: create the engine state with a randomized order. Idempotent. */
export function openDraft(state: SimState, now: number): boolean {
  if (state.draft || !draftRoomOpen(state, now)) return false;
  const draftState = createDraft({
    id: `${state.leagueId}-draft`,
    // The simulator's room runs a pick order; an auction has none and stands
    // in as a snake.
    type: state.settings.draft.order ?? 'snake',
    order: seatOrder(state),
    rounds: state.settings.draft.rounds,
    pickSeconds: state.settings.draft.pickSeconds,
    whenTheClockExpires: state.settings.draft.whenTheClockExpires,
    // The practice room is the real room's twin: a seat whose clock runs out
    // drafts by itself until its manager turns auto off.
    autopilotOnExpiry: true,
  });
  state.draft = { state: draftState };
  state.chat.push(
    system(state, now - 60_000, `The room is open. Draft starts ${startLabel(state)}.`),
  );
  return true;
}

/** The draft's start, in the reader's zone and named (B-181). */
function startLabel(state: SimState): string {
  if (state.draftAt === null) return 'when the commissioner says so';
  return formatInZone(state.draftAt, state.readerZone ?? state.settings.identity.timeZone, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

const system = systemMessage;

/** Fill every open seat with an AI manager (DESIGN.md §2.16) before the first pick. */
function fillOpenSeats(state: SimState): SimTeam[] {
  const filled: SimTeam[] = [];
  let i = hashSeed(state.seed, 'ai-seats') % AI_PRESETS.length;
  for (const team of state.teams) {
    if (team.ownerName !== '') continue;
    const ai = aiForSeat(i++);
    const pro = nflTeams[hashSeed(state.seed, ai.presetId) % nflTeams.length]!;
    // ITS OWN TEAM'S NAME, from its file (B-125), as a real league's seat takes.
    const member = castMember(ai.presetId);
    team.ai = ai;
    team.ownerName = ai.name;
    team.name = member?.teamName ?? `${ai.name}'s Team`;
    team.abbreviation =
      member?.abbreviation ??
      ai.name
        .replace(/[^A-Za-z]/g, '')
        .slice(0, 3)
        .toUpperCase();
    team.colors = { primary: pro.palette.primary, secondary: pro.palette.secondary };
    filled.push(team);
  }
  return filled;
}

/** Start the draft (commissioner intent, or the clock reaching the scheduled time). */
export function startDraft(state: SimState, now: number, by: string): DraftEvent[] {
  if (!state.draft) openDraft(state, now);
  if (!state.draft) return [];
  const filled = fillOpenSeats(state);
  const ai = Object.fromEntries(state.teams.filter((t) => t.ai).map((t) => [t.id, t.ai!]));
  state.draft.state = { ...state.draft.state, ai };
  const r = apply(state.draft.state, { type: 'start', by }, now, draftContext(state));
  state.draft.state = r.state;
  if (filled.length)
    state.chat.push(
      system(
        state,
        now,
        `${filled.map((t) => t.ownerName).join(', ')} ${filled.length === 1 ? 'takes' : 'take'} the open ${filled.length === 1 ? 'seat' : 'seats'}.`,
      ),
    );
  narrate(state, r.events, now);
  return r.events;
}

/** Ken's intent from the room (pick, queue, commissioner controls). */
export function draftIntent(state: SimState, intent: DraftIntent, now: number): DraftEvent[] {
  if (!state.draft)
    return [{ type: 'rejected', code: 'not-live', message: 'The room is not open yet.' }];
  const r = apply(state.draft.state, intent, now, draftContext(state));
  state.draft.state = r.state;
  narrate(state, r.events, now);
  if (r.state.status === 'complete') completeDraft(state, now);
  return r.events;
}

/**
 * Move the room forward to `now`: open it, start it on time, let AI seats
 * and expired clocks pick. Returns true when anything changed. Hosts call
 * this on every clock tick; a jump of hours runs the whole draft.
 */
export function advanceDraft(state: SimState, now: number): boolean {
  let changed = openDraft(state, now);
  if (!state.draft) return changed;
  if (state.draft.state.status === 'lobby' && state.draftAt !== null && now >= state.draftAt) {
    startDraft(state, state.draftAt, 'clock');
    changed = true;
  }
  if (state.draft.state.status !== 'live') return changed;
  const r = advance(state.draft.state, now, draftContext(state));
  if (r.events.length === 0) return changed;
  state.draft.state = r.state;
  narrate(state, r.events, now);
  if (r.state.status === 'complete') completeDraft(state, r.state.completedAt ?? now);
  return true;
}

/** Picks become chat lines; AI managers talk per their dial. */
function narrate(state: SimState, events: DraftEvent[], now: number): void {
  for (const e of events) {
    if (e.type === 'started')
      state.chat.push(system(state, e.at, 'The draft is live. Good luck, everyone.'));
    if (e.type === 'paused') state.chat.push(system(state, e.at, `${e.by} paused the draft.`));
    if (e.type === 'resumed') state.chat.push(system(state, e.at, `${e.by} resumed the draft.`));
    if (e.type === 'undone')
      state.chat.push(system(state, now, `Pick ${e.overall} was undone by the commissioner.`));
    if (e.type === 'completed')
      state.chat.push(
        system(state, e.at, 'That is the draft. Rosters are set. Season starts Thursday.'),
      );
    if (e.type === 'pick.made') {
      const team = state.teams.find((t) => t.id === e.pick.teamId)!;
      const player = state.players.get(e.pick.playerId)!;
      const label = `${e.pick.round}.${String(e.pick.slot).padStart(2, '0')}`;
      const how =
        e.pick.by === 'queue'
          ? ' (from the queue)'
          : e.pick.by === 'plan'
            ? ' (from their plan)'
            : e.pick.by === 'rankings'
              ? ' (autopick)'
              : '';
      state.chat.push(
        system(state, e.pick.at, `${team.ownerName} takes ${player.name}, ${label}${how}.`),
      );
      const member = castMember(team.ai?.presetId);
      if (member !== null) {
        const line = say(
          member,
          'pick',
          {
            player: player.shortName,
            round: e.pick.round,
            overall: e.pick.overall,
            team: team.name,
          },
          talkDraws(state.seed, `talk:${e.pick.overall}`),
        );
        if (line)
          state.chat.push({
            id: `${state.leagueId}-ai-${e.pick.overall}`,
            kind: 'ai',
            author: team.ownerName,
            authorColor: readableColor(team.colors),
            authorFace: botLineFace(team, e.pick.at + 2000),
            body: line,
            at: iso(e.pick.at + 2000),
            reactions: {},
          });
      }
    }
  }
}

/** Rosters from picks, lineups filled, a schedule from this period on, status in-season. */
export function completeDraft(state: SimState, now: number): void {
  if (!state.draft || state.status !== 'setup') return;
  for (const team of state.teams) team.roster = [];
  for (const pick of state.draft.state.picks) {
    state.teams.find((t) => t.id === pick.teamId)!.roster.push(pick.playerId);
  }
  const gameByTeam = new Map(
    state.proGames.flatMap(
      (g) =>
        [
          [g.home, g],
          [g.away, g],
        ] as const,
    ),
  );
  fillLineups(state.players, state.teams, state.rosterPreset, gameByTeam, state.byes);
  state.schedule = buildSchedule(
    state.teams.map((t) => t.id),
    state.currentPeriod,
    14,
  );
  state.status = 'in-season';
  startWaiversAfterDraft(state, now);
  // The recap lands in the channel two minutes after the last pick.
  const recap = draftRecapView(state, now);
  if (recap.available && recap.headline)
    state.chat.push(
      systemMessage(
        state,
        now + 2 * 60_000,
        `Draft recap: ${recap.headline} ${recap.teams
          .slice(0, 3)
          .map((t) => `${t.team.ownerName}: ${t.grade}.`)
          .join(' ')}`,
        {
          kind: 'draft-recap',
          headline: recap.headline,
          lines: recap.teams.slice(0, 3).map((t) => `${t.grade} · ${t.line}`),
        },
      ),
    );
}

/** The draft recap (DRAFT_RECAP.md): grades, steals, and reaches for a draft that ran here. */
/** What a steal is measured against here. The API says the same of its own board. */
const RECAP_BASIS =
  'Measured against the practice league’s own board: every player ordered by ' +
  'what they are worth above the last one at their position somebody starts.';

export function draftRecapView(state: SimState, now: number): DraftRecapView {
  const ds = state.draft?.state ?? null;
  const empty: DraftRecapView = {
    league: leagueRef(state),
    available: false,
    completedAt: null,
    basis: RECAP_BASIS,
    headline: null,
    teams: [],
    steals: [],
    reaches: [],
    mine: null,
    updatedAt: new Date(now).toISOString(),
  };
  if (!ds || ds.status !== 'complete') return empty;
  const ctx = draftContext(state);
  const value = valueCache.get(state)!;
  const rankIndex = new Map(ctx.rankings.map((id, i) => [id, i]));
  const recap = draftRecap(ds, {
    value,
    // A late position's pick is judged neither way (B-521), as a real league's.
    adp: (id) =>
      ctx.latePositions.includes(ctx.positionOf(id))
        ? null
        : adpFor(rankIndex.get(id) ?? ctx.rankings.length, id),
    position: ctx.positionOf,
    playerName: (id) => state.players.get(id)?.name ?? 'somebody',
    ownerName: (teamId) => state.teams.find((t) => t.id === teamId)?.ownerName ?? 'Someone',
    teamName: (teamId) => state.teams.find((t) => t.id === teamId)?.name ?? 'a team',
    style: (teamId, ids) => {
      const ai = state.teams.find((t) => t.id === teamId)?.ai ?? null;
      const style = ai === null ? null : draftStyleOf(ai, ctx, ds.id);
      return style === null ? null : styleRecap(style, ids, ctx);
    },
  });
  if (!recap) return empty;
  const refs = new Map(state.teams.map((t) => [t.id, teamRef(state, t, now)]));
  const note = (n: (typeof recap.steals)[number]) => ({
    overall: n.overall,
    round: n.round,
    team: refs.get(n.teamId)!,
    player: playerSummary(state, state.players.get(n.playerId)!),
    adp: n.adp,
    delta: n.delta,
    // The practice league drafts in a snake: nobody pays.
    price: null,
    line: n.line,
  });
  // Every team's picks, in the order they made them. Carried on the grade so a
  // tap on the recap can answer "what did they actually draft" without a
  // second request — see `DraftTeamGrade.picks`.
  const madeBy = new Map<string, DraftRecapView['teams'][number]['picks']>();
  for (const pick of ds.picks) {
    const player = state.players.get(pick.playerId);
    if (!player) continue;
    const list = madeBy.get(pick.teamId) ?? [];
    list.push({
      overall: pick.overall,
      round: pick.round,
      player: playerSummary(state, player),
      by: pick.by,
      price: null,
    });
    madeBy.set(pick.teamId, list);
  }

  const teams = recap.teams.map((t) => ({
    team: refs.get(t.teamId)!,
    rank: t.rank,
    grade: t.grade,
    line: t.line,
    bestPick: t.bestPick ? note(t.bestPick) : null,
    steals: t.steals,
    reaches: t.reaches,
    positions: t.positions,
    picks: madeBy.get(t.teamId) ?? [],
    style: t.style,
  }));
  return {
    league: leagueRef(state),
    available: true,
    completedAt: ds.completedAt === null ? null : new Date(ds.completedAt).toISOString(),
    basis: RECAP_BASIS,
    headline: recap.headline,
    teams,
    steals: recap.steals.map(note),
    reaches: recap.reaches.map(note),
    mine: teams.find((t) => t.team.isMine) ?? null,
    updatedAt: new Date(now).toISOString(),
  };
}

// ── View ──────────────────────────────────────────────────────────────────

/**
 * A club's bye in the practice world: this week for a club on bye now, and
 * otherwise a week of its own, the same every time. The world has one week of
 * pro games, so the rest of its season is the seed's.
 */
function simBye(state: SimState, club: string): number {
  if (state.byes.includes(club)) return state.currentPeriod;
  const weeks = [5, 6, 7, 8, 9, 10, 11, 12, 13, 14].filter((w) => w !== state.currentPeriod);
  return weeks[hashSeed(state.seed, `bye:${club}`) % weeks.length]!;
}

export function draftView(state: SimState, now: number): DraftView {
  const mine = state.teams.find((t) => t.isMine) ?? null;
  const draft = state.draft;
  const ds = draft?.state ?? null;
  const ctx = draftContext(state);
  const order = ds ? ds.order : state.teams.map((t) => t.id);
  const teamById = new Map(state.teams.map((t) => [t.id, t]));

  const seats = order.map((teamId, i) => {
    const t = teamById.get(teamId)!;
    return {
      team: teamRef(state, t, now),
      slot: i + 1,
      // THE BAND'S NAME, as the chooser will say it: "AI · Sharp". And how it
      // drafts, as the real room says it (AI_MANAGERS.md §4.7).
      ai: t.ai
        ? {
            name: t.ai.name,
            voice: t.ai.voice,
            difficulty: BANDS[bandOf(t.ai.skill)].label,
            style: styleOfSeat(state, t.ai, ds?.id ?? `${state.leagueId}-draft`),
          }
        : null,
      connected: t.isMine || t.ownerName !== '',
      autopilot: Boolean(ds?.autopilot[teamId]),
    };
  });

  const rounds = ds?.rounds ?? state.settings.draft.rounds;
  const n = order.length;
  const overallNow =
    ds && (ds.status === 'live' || ds.status === 'paused') ? currentOverall(ds) : null;
  const board: DraftView['board'] = [];
  for (let overall = 1; overall <= rounds * n; overall++) {
    const pick = ds?.picks.find((p) => p.overall === overall) ?? null;
    const teamId = ds ? teamForPick(ds, overall) : order[(overall - 1) % n]!;
    board.push({
      overall,
      round: Math.ceil(overall / n),
      slot: ((overall - 1) % n) + 1,
      teamId,
      player: pick ? playerSummary(state, state.players.get(pick.playerId)!) : null,
      by: pick?.by ?? null,
      isCurrent: overallNow === overall,
      originalTeamId: null,
      heldBy: null,
      price: null,
    });
  }

  const myNeeds = ds && mine ? Object.keys(needs(ds, mine.id, ctx)) : [];
  const myQueueIds = ds && mine ? (ds.queues[mine.id] ?? []) : [];
  // NUMBERED ON THE WHOLE BOARD, NOT WHAT IS LEFT OF IT (REALTIME.md "what
  // the room must derive differently"): a pick no longer renumbers every
  // player below it, so the practice room reads as the real one does.
  const entries = ctx.rankings.map((id) => {
    const p = state.players.get(id)!;
    return { id, position: p.positions[0], value: p.ppg };
  });
  const ranks = positionRanks(entries);
  const tiers = positionTiers(entries);
  const rankOf = new Map(ctx.rankings.map((id, i) => [id, i]));
  const available: DraftView['available'] = ctx.rankings
    .filter((id) => !ds || !isTaken(ds, id))
    .map((id) => {
      const p = state.players.get(id)!;
      const i = rankOf.get(id)!;
      const proj = projection(state, p);
      return {
        player: playerSummary(state, p),
        rank: i + 1,
        projection: proj,
        adp: adpFor(i, id),
        tier: tiers.get(id) ?? 1,
        positionRank: ranks.get(id) ?? null,
        bye: simBye(state, p.proTeam),
        // The practice world's last season is its players' own average.
        last: {
          season: state.season - 1,
          games: 17,
          points: Math.round(p.ppg * 17 * 10) / 10,
          perGame: Math.round(p.ppg * 10) / 10,
        },
        queued: myQueueIds.includes(id),
        fillsNeed: myNeeds.includes(p.positions[0]),
        value: null,
      };
    });

  const slotDefs = new Map(football.slots.map((s) => [s.id, s]));
  const myPicks =
    ds && mine ? ds.picks.filter((p) => p.teamId === mine.id).map((p) => p.playerId) : [];
  const unplaced = [...myPicks];
  const myRoster: DraftView['myRoster'] = [];
  for (const { slot, count } of state.settings.roster.slots) {
    const def = slotDefs.get(slot);
    if (!def) continue;
    for (let i = 0; i < count; i++) {
      let chosen: string | null = null;
      if (def.kind === 'starter') {
        chosen =
          unplaced.find((id) => def.eligible.includes(state.players.get(id)!.positions[0])) ?? null;
      } else if (def.kind === 'bench') chosen = unplaced[0] ?? null;
      if (chosen) unplaced.splice(unplaced.indexOf(chosen), 1);
      myRoster.push({
        slot,
        slotName: def.name,
        player: chosen ? playerSummary(state, state.players.get(chosen)!) : null,
      });
    }
  }

  const current =
    ds && overallNow !== null && overallNow <= rounds * n
      ? {
          overall: overallNow,
          round: Math.ceil(overallNow / n),
          teamId: currentTeam(ds)!,
          isMine: mine !== null && currentTeam(ds) === mine.id,
          deadlineAt: ds.deadlineAt === null ? null : iso(ds.deadlineAt),
          sniped: wantedTaken(ds, currentTeam(ds)!, draftContext(state)),
        }
      : null;
  const upcoming =
    ds && overallNow !== null
      ? [1, 2, 3]
          .map((k) => overallNow + k)
          .filter((o) => o <= rounds * n)
          .map((o) => ({ overall: o, teamId: teamForPick(ds, o) }))
      : [];
  const last = ds?.picks[ds.picks.length - 1];

  return {
    league: leagueRef(state),
    // The practice league's clock never stops for the night, and it is no mock.
    overnightPause: null,
    mockRoom: null,
    status: ds?.status ?? 'lobby',
    type: ds?.type ?? 'snake',
    // The practice league's room is a live draft, whatever its rules say.
    mode: 'live',
    rounds,
    pickSeconds: ds?.pickSeconds ?? state.settings.draft.pickSeconds,
    whenTheClockExpires: ds?.whenTheClockExpires ?? state.settings.draft.whenTheClockExpires,
    basis:
      'The practice league’s own board: every player ordered by what he is worth over ' +
      'the last one at his position a team starts.',
    byeWeeks: Object.fromEntries(nflTeams.map((t) => [t.id, simBye(state, t.id)])),
    scheduledAt: state.draftAt === null ? null : iso(state.draftAt),
    roomOpen: draftRoomOpen(state, now) || Boolean(ds),
    seats,
    current,
    upcoming,
    board,
    available,
    myTeamId: mine?.id ?? null,
    myQueue: myQueueIds.map((id) => playerSummary(state, state.players.get(id)!)),
    myRoster,
    myNeeds,
    chat: chatView(state),
    isCommissioner: state.isCommissioner,
    lastPick: last ? board.find((c) => c.overall === last.overall)! : null,
    // EVERY OTHER SEAT IS A BOT, and bots do not trade in the room.
    trading: { allowed: false, offers: [], trades: [] },
    // The practice league drafts in a snake.
    auction: null,
    updatedAt: iso(now),
  };
}
